import { c as _c } from "react/compiler-runtime";
import { useMutation } from '@tanstack/react-query';
import classNames from 'classnames';
import { type Dispatch, type ElementType, type SetStateAction, useRef, useState, useTransition } from 'react';
import { useForm } from 'react-hook-form';
import { type PixelCrop } from 'react-image-crop';
import { ButtonVariant } from '@shared/ui-components/button/buttonVariant';
import { FormWrapper } from '@shared/ui-components/form-wrapper/form-wrapper';
import { PICTURE_UPLOAD_SERVER_ERROR_KEY } from '@shared/utils/constants/fileUpload';
import { formBaseConfig } from '@shared/utils/constants/formBaseConfig';
import { ButtonType } from '@shared/utils/enums/button-type';
import { getCroppedFile } from '@shared/utils/helpers/get-cropped-file';
import { useProfilePictureUpload } from '@shared/utils/hooks/use-profile-picture-upload';
import { type ProfilePictureData } from '@shared/utils/types/profile-picture';
import { type ServerActionResponse } from '@shared/utils/types/server-action';
import { UploadPhotoDialogActions } from '../upload-photo-dialog-actions/upload-photo-dialog-actions';
import { type PhotoUploadResponse, type ProfilePhotoContainerContent } from './profile-photo-container';
import { ProfilePhotoCropper } from './profile-photo-cropper';
import { ProfilePhotoDialogContent } from './profile-photo-dialog-content';
import styles from './profile-photo-dialog.module.scss';
const usePictureUploadMutation = t0 => {
  const $ = _c(3);
  const {
    handleSubmitProfilePicture,
    onPhotoUploadSuccess
  } = t0;
  let t1;
  if ($[0] !== handleSubmitProfilePicture || $[1] !== onPhotoUploadSuccess) {
    t1 = {
      mutationFn: handleSubmitProfilePicture,
      onSuccess: onPhotoUploadSuccess
    };
    $[0] = handleSubmitProfilePicture;
    $[1] = onPhotoUploadSuccess;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return useMutation(t1);
};
const convertFileToFileList = (file: File) => {
  const dataTransfer = new DataTransfer();
  dataTransfer.items.add(file);
  return dataTransfer.files;
};
type ProfilePhotoDialogProps = {
  cmsContent: ProfilePhotoContainerContent;
  DialogComponent: ElementType;
  handleModalClose: () => void;
  handleProfilePictureDelete: () => Promise<ServerActionResponse>;
  handleSubmitProfilePicture: (formData: BodyInit) => Promise<PhotoUploadResponse>;
  initialProfilePicture: string | undefined;
  setProfilePictureInSection: Dispatch<SetStateAction<string | undefined>>;
};
export const ProfilePhotoDialog = t0 => {
  const $ = _c(96);
  const {
    cmsContent,
    DialogComponent: Dialog,
    handleModalClose,
    handleProfilePictureDelete,
    handleSubmitProfilePicture,
    initialProfilePicture,
    setProfilePictureInSection
  } = t0;
  const [isDeletePending, startDeleteTransition] = useTransition();
  const [croppedFile, setCroppedFile] = useState(null);
  const imgRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ...formBaseConfig,
      defaultValues: {
        file: null
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const formMethods = useForm(t1);
  let t2;
  if ($[1] !== cmsContent.fileSizeError || $[2] !== cmsContent.fileTypeError || $[3] !== initialProfilePicture) {
    t2 = {
      fileSizeErrorMessage: cmsContent.fileSizeError,
      fileTypeErrorMessage: cmsContent.fileTypeError,
      initialProfilePicture
    };
    $[1] = cmsContent.fileSizeError;
    $[2] = cmsContent.fileTypeError;
    $[3] = initialProfilePicture;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    previewPictureSource,
    rules,
    updatePreviewPictureSource,
    validateFileSize
  } = useProfilePictureUpload(t2);
  let t3;
  if ($[5] !== formMethods || $[6] !== handleModalClose) {
    t3 = () => {
      formMethods.reset();
      setCroppedFile(null);
      handleModalClose();
    };
    $[5] = formMethods;
    $[6] = handleModalClose;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const resetAndCloseModal = t3;
  let t4;
  if ($[8] !== cmsContent.fileServerError || $[9] !== croppedFile || $[10] !== formMethods || $[11] !== resetAndCloseModal || $[12] !== setProfilePictureInSection) {
    t4 = response => {
      if (response === null) {
        return;
      }
      if (response && ("messageTranslationKey" in response || "message" in response)) {
        formMethods.setError("file", {
          message: response.messageTranslationKey === PICTURE_UPLOAD_SERVER_ERROR_KEY ? cmsContent.fileServerError : response.message,
          type: PICTURE_UPLOAD_SERVER_ERROR_KEY
        });
        return;
      }
      const croppedPictureSource = croppedFile ? URL.createObjectURL(croppedFile) : "";
      setProfilePictureInSection(croppedPictureSource);
      resetAndCloseModal();
    };
    $[8] = cmsContent.fileServerError;
    $[9] = croppedFile;
    $[10] = formMethods;
    $[11] = resetAndCloseModal;
    $[12] = setProfilePictureInSection;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== handleSubmitProfilePicture || $[15] !== t4) {
    t5 = {
      handleSubmitProfilePicture,
      onPhotoUploadSuccess: t4
    };
    $[14] = handleSubmitProfilePicture;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const {
    isPending: isUploadPhotoMutationPending,
    mutate: uploadPhoto
  } = usePictureUploadMutation(t5);
  let t6;
  if ($[17] !== croppedFile || $[18] !== uploadPhoto) {
    t6 = async () => {
      if (!croppedFile) {
        return;
      }
      const formData = new FormData();
      formData.append("file", croppedFile);
      uploadPhoto(formData);
    };
    $[17] = croppedFile;
    $[18] = uploadPhoto;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const onSubmit = t6;
  let t7;
  if ($[20] !== formMethods || $[21] !== validateFileSize) {
    t7 = async crop => {
      if (!imgRef.current) {
        return;
      }
      const file = await getCroppedFile(imgRef.current, crop);
      const fileList = convertFileToFileList(file);
      const {
        fileSizeErrorMessage,
        isValidFileSize
      } = validateFileSize(fileList);
      if (!isValidFileSize) {
        formMethods.setError("file", {
          message: fileSizeErrorMessage,
          type: "manual"
        });
        return;
      }
      setCroppedFile(file);
      formMethods.clearErrors("file");
      formMethods.setValue("file", fileList, {
        shouldDirty: true,
        shouldTouch: true
      });
    };
    $[20] = formMethods;
    $[21] = validateFileSize;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const handleCropChange = t7;
  const fileInputError = formMethods.formState.errors.file?.message;
  let t8;
  if ($[23] !== croppedFile || $[24] !== formMethods) {
    t8 = !croppedFile || !formMethods.formState.isDirty || !formMethods.getValues("file")?.length;
    $[23] = croppedFile;
    $[24] = formMethods;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const t9 = previewPictureSource ? cmsContent.save : undefined;
  const t10 = previewPictureSource ? ButtonType.SUBMIT : undefined;
  let t11;
  if ($[26] !== t10 || $[27] !== t8 || $[28] !== t9) {
    t11 = {
      primaryButtonDisabled: t8,
      primaryButtonLabel: t9,
      primaryButtonType: t10
    };
    $[26] = t10;
    $[27] = t8;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const primaryButtonProps = t11;
  let T0;
  let T1;
  let fileInputProps;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let tertiaryButtonProps;
  if ($[30] !== Dialog || $[31] !== cmsContent.add || $[32] !== cmsContent.change || $[33] !== cmsContent.close || $[34] !== cmsContent.modalTitle || $[35] !== cmsContent.remove || $[36] !== fileInputError || $[37] !== handleProfilePictureDelete || $[38] !== initialProfilePicture || $[39] !== isDeletePending || $[40] !== isUploadPhotoMutationPending || $[41] !== previewPictureSource || $[42] !== resetAndCloseModal || $[43] !== rules.validate.acceptedFormats || $[44] !== setProfilePictureInSection || $[45] !== updatePreviewPictureSource) {
    tertiaryButtonProps = (initialProfilePicture || previewPictureSource) && {
      onTertiaryButtonClick: () => startDeleteTransition(async () => {
        const result = await handleProfilePictureDelete();
        if (result.status === 200) {
          setProfilePictureInSection(undefined);
          resetAndCloseModal();
        }
      }),
      tertiaryButtonLabel: cmsContent.remove
    };
    const t17 = fileInputError !== undefined;
    const t18 = initialProfilePicture || previewPictureSource ? cmsContent.change : cmsContent.add;
    let t19;
    if ($[55] !== rules.validate.acceptedFormats) {
      t19 = {
        validate: {
          acceptedFormats: rules.validate.acceptedFormats
        }
      };
      $[55] = rules.validate.acceptedFormats;
      $[56] = t19;
    } else {
      t19 = $[56];
    }
    const t20 = initialProfilePicture || previewPictureSource ? ButtonVariant.Secondary : undefined;
    let t21;
    if ($[57] !== t17 || $[58] !== t18 || $[59] !== t19 || $[60] !== t20 || $[61] !== updatePreviewPictureSource) {
      t21 = {
        dataTestId: "file-input",
        fileInputError: t17,
        fileInputLabel: t18,
        fileInputName: "file",
        fileInputRules: t19,
        fileInputVariant: t20,
        name: "file",
        onFileInputChange: updatePreviewPictureSource
      };
      $[57] = t17;
      $[58] = t18;
      $[59] = t19;
      $[60] = t20;
      $[61] = updatePreviewPictureSource;
      $[62] = t21;
    } else {
      t21 = $[62];
    }
    fileInputProps = t21;
    T1 = Dialog;
    t13 = cmsContent.close;
    t14 = isUploadPhotoMutationPending || isDeletePending;
    t15 = resetAndCloseModal;
    t16 = cmsContent.modalTitle;
    T0 = FormWrapper;
    t12 = classNames(styles.form, {
      [styles["form--wide"]]: tertiaryButtonProps
    });
    $[30] = Dialog;
    $[31] = cmsContent.add;
    $[32] = cmsContent.change;
    $[33] = cmsContent.close;
    $[34] = cmsContent.modalTitle;
    $[35] = cmsContent.remove;
    $[36] = fileInputError;
    $[37] = handleProfilePictureDelete;
    $[38] = initialProfilePicture;
    $[39] = isDeletePending;
    $[40] = isUploadPhotoMutationPending;
    $[41] = previewPictureSource;
    $[42] = resetAndCloseModal;
    $[43] = rules.validate.acceptedFormats;
    $[44] = setProfilePictureInSection;
    $[45] = updatePreviewPictureSource;
    $[46] = T0;
    $[47] = T1;
    $[48] = fileInputProps;
    $[49] = t12;
    $[50] = t13;
    $[51] = t14;
    $[52] = t15;
    $[53] = t16;
    $[54] = tertiaryButtonProps;
  } else {
    T0 = $[46];
    T1 = $[47];
    fileInputProps = $[48];
    t12 = $[49];
    t13 = $[50];
    t14 = $[51];
    t15 = $[52];
    t16 = $[53];
    tertiaryButtonProps = $[54];
  }
  let t17;
  if ($[63] !== cmsContent.modalDescription || $[64] !== cmsContent.photoCropAriaLiveText || $[65] !== cmsContent.pictureDescription || $[66] !== cmsContent.pictureFileInputHint || $[67] !== cmsContent.upload) {
    t17 = {
      modalDescription: cmsContent.modalDescription,
      photoCropAriaLiveText: cmsContent.photoCropAriaLiveText,
      pictureDescription: cmsContent.pictureDescription,
      pictureFileInputHint: cmsContent.pictureFileInputHint,
      upload: cmsContent.upload
    };
    $[63] = cmsContent.modalDescription;
    $[64] = cmsContent.photoCropAriaLiveText;
    $[65] = cmsContent.pictureDescription;
    $[66] = cmsContent.pictureFileInputHint;
    $[67] = cmsContent.upload;
    $[68] = t17;
  } else {
    t17 = $[68];
  }
  let t18;
  if ($[69] !== fileInputError || $[70] !== previewPictureSource || $[71] !== t17) {
    t18 = <ProfilePhotoDialogContent cmsContent={t17} imageSrc={previewPictureSource} profileUploadError={fileInputError} />;
    $[69] = fileInputError;
    $[70] = previewPictureSource;
    $[71] = t17;
    $[72] = t18;
  } else {
    t18 = $[72];
  }
  let t19;
  if ($[73] !== cmsContent.photoCropAriaLiveText || $[74] !== handleCropChange || $[75] !== previewPictureSource) {
    t19 = previewPictureSource && <ProfilePhotoCropper cropAriaLiveText={cmsContent.photoCropAriaLiveText} imgRef={imgRef} onCropChange={handleCropChange} src={previewPictureSource} />;
    $[73] = cmsContent.photoCropAriaLiveText;
    $[74] = handleCropChange;
    $[75] = previewPictureSource;
    $[76] = t19;
  } else {
    t19 = $[76];
  }
  let t20;
  if ($[77] !== fileInputProps || $[78] !== primaryButtonProps || $[79] !== tertiaryButtonProps) {
    t20 = <UploadPhotoDialogActions {...primaryButtonProps} {...fileInputProps} {...tertiaryButtonProps} />;
    $[77] = fileInputProps;
    $[78] = primaryButtonProps;
    $[79] = tertiaryButtonProps;
    $[80] = t20;
  } else {
    t20 = $[80];
  }
  let t21;
  if ($[81] !== T0 || $[82] !== formMethods || $[83] !== onSubmit || $[84] !== t12 || $[85] !== t18 || $[86] !== t19 || $[87] !== t20) {
    t21 = <T0 className={t12} dataTestId="profile-photo-container-form" formMethods={formMethods} onSubmit={onSubmit}>{t18}{t19}{t20}</T0>;
    $[81] = T0;
    $[82] = formMethods;
    $[83] = onSubmit;
    $[84] = t12;
    $[85] = t18;
    $[86] = t19;
    $[87] = t20;
    $[88] = t21;
  } else {
    t21 = $[88];
  }
  let t22;
  if ($[89] !== T1 || $[90] !== t13 || $[91] !== t14 || $[92] !== t15 || $[93] !== t16 || $[94] !== t21) {
    t22 = <T1 closeButtonLabel={t13} isLoading={t14} onModalClose={t15} title={t16}>{t21}</T1>;
    $[89] = T1;
    $[90] = t13;
    $[91] = t14;
    $[92] = t15;
    $[93] = t16;
    $[94] = t21;
    $[95] = t22;
  } else {
    t22 = $[95];
  }
  return t22;
};